import React, { useEffect, useState } from 'react';
import { MessageSquare, Send, Loader2, ArrowLeft, User, Search, AlertCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';

export default function AdminMessages() {
  const [conversations, setConversations] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Chat view state
  const [selectedConvo, setSelectedConvo] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [loadingChat, setLoadingChat] = useState(false);
  const [newReply, setNewReply] = useState('');
  const [sending, setSending] = useState(false);
  const [replyError, setReplyError] = useState('');

  useEffect(() => {
    fetchConversations();
  }, []);

  const fetchConversations = async () => {
    try {
      const [msgsRes, usersRes] = await Promise.all([
        supabase.from('messages').select('*').order('created_at', { ascending: false }),
        supabase.from('profiles').select('id, first_name, last_name, avatar_url'),
      ]);

      if (msgsRes.error) throw msgsRes.error;
      if (usersRes.error) throw usersRes.error;

      setUsers(usersRes.data || []);
      const allMessages = msgsRes.data || [];

      // Group messages by user_id
      const grouped = {};
      allMessages.forEach(msg => {
        if (!grouped[msg.user_id]) {
          grouped[msg.user_id] = [];
        }
        grouped[msg.user_id].push(msg);
      });

      // Build conversation list
      const convos = Object.keys(grouped).map(userId => {
        const msgs = grouped[userId];
        const user = (usersRes.data || []).find(u => u.id === userId);
        const lastMsg = msgs[0]; // Already sorted desc
        
        // Count unreplied: user messages where admin_has_replied is false
        const unrepliedCount = msgs.filter(m => !m.is_from_admin && !m.admin_has_replied).length;

        const fn = (user?.first_name || '').trim();
        const ln = (user?.last_name || '').trim();
        const name = (fn && fn !== 'null' && fn !== 'Unknown') ? `${fn} ${ln && ln !== 'null' ? ln : ''}`.trim() : (user?.email ? user.email.split('@')[0].charAt(0).toUpperCase() + user.email.split('@')[0].slice(1) : 'User');
        const initial = name ? name[0].toUpperCase() : 'U';

        return {
          user_id: userId,
          user_name: name,
          avatar_url: user?.avatar_url,
          initial: initial,
          last_message: lastMsg.message,
          last_message_time: lastMsg.created_at,
          is_last_from_admin: lastMsg.is_from_admin,
          unreplied_count: unrepliedCount,
          total_messages: msgs.length,
        };
      });

      // Sort: unreplied first, then by last message time
      convos.sort((a, b) => {
        if (a.unreplied_count > 0 && b.unreplied_count === 0) return -1;
        if (a.unreplied_count === 0 && b.unreplied_count > 0) return 1;
        return new Date(b.last_message_time) - new Date(a.last_message_time);
      });

      setConversations(convos);
    } catch (err) {
      console.error('Error fetching conversations:', err);
    } finally {
      setLoading(false);
    }
  };

  const openChat = async (convo) => {
    setSelectedConvo(convo);
    setLoadingChat(true);
    try {
      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .eq('user_id', convo.user_id)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setChatMessages(data || []);
    } catch (err) {
      console.error('Error fetching chat:', err);
    } finally {
      setLoadingChat(false);
    }
  };

  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!newReply.trim() || !selectedConvo) return;

    setSending(true);
    setReplyError('');
    const replyText = newReply.trim();
    setNewReply('');

    try {
      // Insert the admin reply
      const { data: newMsg, error: insertError } = await supabase
        .from('messages')
        .insert({
          user_id: selectedConvo.user_id,
          message: replyText,
          is_from_admin: true,
          admin_has_replied: true,
        })
        .select()
        .single();

      if (insertError) throw insertError;

      // Mark ALL unreplied messages from this user as replied
      const { error: updateError } = await supabase
        .from('messages')
        .update({ admin_has_replied: true })
        .eq('user_id', selectedConvo.user_id)
        .eq('is_from_admin', false)
        .eq('admin_has_replied', false);

      if (updateError) throw updateError;

      // Send push notification to user
      supabase.functions.invoke('send-push', {
        body: {
          user_id: selectedConvo.user_id,
          title: 'New Message from Specialist 💬',
          body: replyText,
          url: '/dashboard/messages'
        }
      }).catch(e => console.error('Error triggering push:', e));

      // Update local state
      setChatMessages([...chatMessages, newMsg]);
      
      // Refresh conversation list to update badges
      await fetchConversations();

      // Keep the selected convo open
      setSelectedConvo(prev => prev ? { ...prev, unreplied_count: 0 } : null);
    } catch (err) {
      console.error('Error sending reply:', err);
      setNewReply(replyText);
      setReplyError('Failed to send reply. Please try again.');
      setTimeout(() => { setReplyError(prev => prev === 'Failed to send reply. Please try again.' ? '' : prev); }, 5000);
    } finally {
      setSending(false);
    }
  };

  const totalUnreplied = conversations.filter(c => c.unreplied_count > 0).length;

  const filteredConversations = conversations.filter(c =>
    c.user_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatTime = (dateStr) => {
    const d = new Date(dateStr);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    if (isToday) {
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  // Chat view
  if (selectedConvo) {
    return (
      <div className="h-[calc(100vh-8rem)] flex flex-col">
        {/* Chat Header */}
        <div className="bg-white rounded-t-2xl border border-slate-200 border-b-0 p-4 flex items-center gap-3">
          <button
            onClick={() => { setSelectedConvo(null); fetchConversations(); }}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </button>
          {selectedConvo.avatar_url ? (
            <img src={selectedConvo.avatar_url} alt="" className="w-10 h-10 rounded-full object-cover" />
          ) : (
            <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold">
              {selectedConvo.initial}
            </div>
          )}
          <div>
            <h3 className="font-bold text-slate-900">{selectedConvo.user_name}</h3>
            <p className="text-xs text-slate-500">{selectedConvo.total_messages} messages</p>
          </div>
        </div>

        {/* Chat Messages */}
        <div className="flex-1 bg-white border-x border-slate-200 overflow-y-auto p-4 space-y-4">
          {loadingChat ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
            </div>
          ) : (
            chatMessages.map((msg) => (
              <div key={msg.id} className={`flex ${msg.is_from_admin ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[75%] px-4 py-3 rounded-2xl ${
                  msg.is_from_admin
                    ? 'bg-indigo-600 text-white rounded-br-none'
                    : 'bg-slate-100 text-slate-800 rounded-bl-none'
                }`}>
                  <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                  <p className={`text-[10px] mt-1 text-right ${
                    msg.is_from_admin ? 'text-indigo-200' : 'text-slate-400'
                  }`}>
                    {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Reply Input */}
        <div className="bg-white rounded-b-2xl border border-slate-200 border-t-0 p-4">
          {replyError && (
            <div className="mb-2 text-rose-600 text-xs font-semibold flex items-center gap-1.5 animate-fadeIn">
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
              <span>{replyError}</span>
            </div>
          )}
          <form onSubmit={handleSendReply} className="flex gap-2">
            <input
              type="text"
              value={newReply}
              onChange={(e) => setNewReply(e.target.value)}
              placeholder="Type your reply..."
              className="flex-1 px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 bg-slate-50"
            />
            <button
              type="submit"
              disabled={sending || !newReply.trim()}
              className="px-5 py-3 bg-indigo-600 text-white rounded-xl font-bold flex items-center gap-2 hover:bg-indigo-700 transition-colors disabled:opacity-50"
            >
              {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Conversation List (WhatsApp style)
  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-indigo-950">
            Messages
            {totalUnreplied > 0 && (
              <span className="ml-3 inline-flex items-center justify-center w-7 h-7 bg-red-500 text-white text-sm font-bold rounded-full">
                {totalUnreplied}
              </span>
            )}
          </h1>
          <p className="mt-1 text-slate-500">
            {totalUnreplied > 0
              ? `${totalUnreplied} conversation${totalUnreplied > 1 ? 's' : ''} awaiting your reply.`
              : 'All conversations are up to date.'
            }
          </p>
        </div>

        <div className="relative w-full sm:max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search conversations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 shadow-sm"
          />
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden divide-y divide-slate-100">
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
          </div>
        ) : filteredConversations.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <MessageSquare className="w-12 h-12 text-slate-300 mb-3" />
            <p className="text-slate-500 font-medium">No conversations yet.</p>
          </div>
        ) : (
          filteredConversations.map((convo) => (
            <button
              key={convo.user_id}
              onClick={() => openChat(convo)}
              className={`w-full flex items-center gap-4 p-4 hover:bg-slate-50 transition-colors text-left ${
                convo.unreplied_count > 0 ? 'bg-indigo-50/30' : ''
              }`}
            >
              {/* Avatar */}
              <div className="relative flex-shrink-0">
                {convo.avatar_url ? (
                  <img src={convo.avatar_url} alt="" className="w-12 h-12 rounded-full object-cover" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold text-lg">
                    {convo.initial}
                  </div>
                )}
                {convo.unreplied_count > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                    {convo.unreplied_count}
                  </span>
                )}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <span className={`font-semibold truncate ${convo.unreplied_count > 0 ? 'text-slate-900' : 'text-slate-700'}`}>
                    {convo.user_name}
                  </span>
                  <span className="text-xs text-slate-400 flex-shrink-0 ml-2">
                    {formatTime(convo.last_message_time)}
                  </span>
                </div>
                <p className={`text-sm truncate ${convo.unreplied_count > 0 ? 'text-slate-800 font-medium' : 'text-slate-500'}`}>
                  {convo.is_last_from_admin ? 'You: ' : ''}{convo.last_message}
                </p>
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
}
